import { useEffect, useRef } from "react";

/**
 * Pan/zoom for one Raw Surface Analysis chart, split by input rather than by a persistent mode
 * switch - so the existing drag-to-select-a-shaft-range plugin (src/plugin/selector.js/handler.js,
 * shared with Scan Analysis's ScatterGraph.js) needs zero changes and plain click-drag keeps
 * working exactly as it always has:
 *
 * - Zoom: any wheel event with ctrlKey true. Covers two real inputs with one check - explicitly
 *   holding Ctrl while scrolling on any mouse, and a trackpad pinch gesture (macOS, and Chromium/
 *   Firefox on Windows precision touchpads, synthesize ctrlKey on the wheel event during a pinch
 *   specifically so web apps can tell it apart from ordinary scrolling).
 * - Pan: a plain wheel event (no Ctrl) - translates deltaX/deltaY into a shift of the visible
 *   range instead of letting the page scroll. Covers a trackpad two-finger swipe and a Magic
 *   Mouse's one-finger surface swipe for free, plus a plain mouse's scroll wheel for vertical pan.
 * - Pan via drag (fallback for fine control, or a mouse with no gesture surface): hold Space and
 *   click-drag. Implemented as a capture-phase listener on an ANCESTOR of the chart's canvas -
 *   capture-phase listeners on an ancestor run before the event ever reaches the canvas, so this
 *   reliably keeps selector.js's bubble-phase listeners (attached directly on the canvas) from
 *   ever seeing the event while Space is held, without modifying that shared file at all.
 *
 * Exact pan direction (which way the view moves for a given drag/swipe direction) is a best-guess
 * convention below, not verified against real hardware interactively - flip the sign on shiftX/
 * shiftY if manual testing shows it feels backwards.
 *
 * `attach(chart, container)` must be called synchronously, right after constructing the Chart.js
 * instance, from within the SAME effect that does `new Chart(...)` - not wired up reactively via
 * an effect keyed on a ref value. A ref mutation (chartRef.current = ...) doesn't reliably
 * re-trigger a dependent effect at the right time (React only re-evaluates a dependency array on
 * a render that happens to occur afterward, not the instant the ref changes) - this is the same
 * class of timing bug the drilldown's canvas-mount race turned out to be, so it's avoided here by
 * construction instead of by an effect racing the chart's own setup.
 */
export default function useChartPanZoom({ xBounds, onXRangeChange }) {
    const spaceHeldRef = useRef(false);
    const dragStateRef = useRef(null);
    const pinchStateRef = useRef(null);
    const chartRef = useRef(null);
    const containerRef = useRef(null);

    const reportXRange = (min, max) => {
        if (onXRangeChange) onXRangeChange(min, max);
    };

    /** Restores the chart's default view: the fixed physical X domain, and Y back to auto-scale. */
    const resetView = () => {
        const chart = chartRef.current;
        if (!chart) return;
        chart.options.scales.x.min = xBounds.min;
        chart.options.scales.x.max = xBounds.max;
        delete chart.options.scales.y.min;
        delete chart.options.scales.y.max;
        chart.update("none");
        reportXRange(xBounds.min, xBounds.max);
    };

    /** Imperatively applied by a sibling chart's pan/zoom, to keep the three X-windows in sync. */
    const setXRange = (min, max) => {
        const chart = chartRef.current;
        if (!chart) return;
        chart.options.scales.x.min = min;
        chart.options.scales.x.max = max;
        chart.update("none");
    };

    // Space-held tracking, global (window-level) since the key can be pressed while the mouse is
    // anywhere. Independent of the chart/container being ready yet, so this is safe to set up
    // unconditionally rather than through `attach`.
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.code === "Space" && !e.repeat) {
                spaceHeldRef.current = true;
                if (containerRef.current) containerRef.current.style.cursor = "grab";
            }
        };
        const handleKeyUp = (e) => {
            if (e.code === "Space") {
                spaceHeldRef.current = false;
                if (containerRef.current && !dragStateRef.current) containerRef.current.style.cursor = "";
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        window.addEventListener("keyup", handleKeyUp);
        return () => {
            window.removeEventListener("keydown", handleKeyDown);
            window.removeEventListener("keyup", handleKeyUp);
        };
    }, []);

    const attach = (chart, container) => {
        chartRef.current = chart;
        containerRef.current = container;
        const canvas = chart.canvas;

        // Wheel: ctrlKey -> cursor-anchored zoom, plain -> pan. Bubble phase, directly on the
        // canvas - wheel never conflicts with selector.js, which only listens for mouse button
        // events, so no capture-phase trickery is needed here.
        const handleWheel = (e) => {
            const c = chartRef.current;
            if (!c) return;
            e.preventDefault();

            const xScale = c.scales.x;
            const yScale = c.scales.y;
            const rect = canvas.getBoundingClientRect();
            const px = e.clientX - rect.left;
            const py = e.clientY - rect.top;

            if (e.ctrlKey) {
                const dataX = xScale.getValueForPixel(px);
                const dataY = yScale.getValueForPixel(py);
                const factor = Math.exp(-e.deltaY * 0.0015);
                const newXMin = dataX - (dataX - xScale.min) / factor;
                const newXMax = dataX + (xScale.max - dataX) / factor;
                const newYMin = dataY - (dataY - yScale.min) / factor;
                const newYMax = dataY + (yScale.max - dataY) / factor;
                c.options.scales.x.min = newXMin;
                c.options.scales.x.max = newXMax;
                c.options.scales.y.min = newYMin;
                c.options.scales.y.max = newYMax;
                c.update("none");
                reportXRange(newXMin, newXMax);
            } else {
                const dataPerPixelX = (xScale.max - xScale.min) / c.chartArea.width;
                const dataPerPixelY = (yScale.max - yScale.min) / c.chartArea.height;
                let shiftX;
                let shiftY;
                if (e.deltaX === 0) {
                    // A plain mouse's scroll wheel only ever reports deltaY - no horizontal axis
                    // at all. Treating that lone axis as panning X (shaft position) rather than Y
                    // is deliberate: X is the axis actually worth navigating by hand here (Y
                    // auto-scales to whatever's in view anyway). A real trackpad/Magic Mouse
                    // swipe reports a nonzero deltaX and falls into the branch below instead,
                    // panning each axis independently to match the swipe's own direction.
                    shiftX = e.deltaY * dataPerPixelX;
                    shiftY = 0;
                } else {
                    shiftX = e.deltaX * dataPerPixelX;
                    shiftY = -e.deltaY * dataPerPixelY;
                }
                const newXMin = xScale.min + shiftX;
                const newXMax = xScale.max + shiftX;
                c.options.scales.x.min = newXMin;
                c.options.scales.x.max = newXMax;
                c.options.scales.y.min = yScale.min + shiftY;
                c.options.scales.y.max = yScale.max + shiftY;
                c.update("none");
                reportXRange(newXMin, newXMax);
            }
        };

        // Safari doesn't synthesize ctrlKey on the wheel event during a trackpad pinch the way
        // Chromium/Firefox do - it fires its own proprietary gesturestart/gesturechange/gestureend
        // events instead (WebKit-only; no standard equivalent, and other browsers never fire
        // these at all, so adding this is purely additive - there's no double-handling risk).
        // e.scale is cumulative since gesturestart, not a per-event delta, so the anchor point and
        // starting bounds are captured once at gesturestart and every gesturechange recomputes
        // from that fixed starting point rather than compounding off the previous frame.
        const handleGestureStart = (e) => {
            e.preventDefault();
            const c = chartRef.current;
            if (!c) return;
            const rect = canvas.getBoundingClientRect();
            const px = e.clientX - rect.left;
            const py = e.clientY - rect.top;
            pinchStateRef.current = {
                anchorDataX: c.scales.x.getValueForPixel(px),
                anchorDataY: c.scales.y.getValueForPixel(py),
                startXMin: c.scales.x.min,
                startXMax: c.scales.x.max,
                startYMin: c.scales.y.min,
                startYMax: c.scales.y.max,
            };
        };

        const handleGestureChange = (e) => {
            e.preventDefault();
            const c = chartRef.current;
            const pinch = pinchStateRef.current;
            if (!c || !pinch) return;
            const { anchorDataX, anchorDataY, startXMin, startXMax, startYMin, startYMax } = pinch;
            const factor = e.scale; // >1 = fingers spread apart = zoom in
            const newXMin = anchorDataX - (anchorDataX - startXMin) / factor;
            const newXMax = anchorDataX + (startXMax - anchorDataX) / factor;
            const newYMin = anchorDataY - (anchorDataY - startYMin) / factor;
            const newYMax = anchorDataY + (startYMax - anchorDataY) / factor;
            c.options.scales.x.min = newXMin;
            c.options.scales.x.max = newXMax;
            c.options.scales.y.min = newYMin;
            c.options.scales.y.max = newYMax;
            c.update("none");
            reportXRange(newXMin, newXMax);
        };

        const handleGestureEnd = (e) => {
            e.preventDefault();
            pinchStateRef.current = null;
        };

        // Space+drag pan, capture-phase on the container (an ancestor of the canvas) so
        // selector.js's own listeners (attached directly on the canvas, bubble phase) never see
        // the event while Space is held.
        const handleMouseDown = (e) => {
            if (!spaceHeldRef.current) return; // not our gesture - let select-drag proceed untouched
            const chart = chartRef.current;
            if (!chart) return;
            e.stopPropagation();
            e.preventDefault();
            dragStateRef.current = {
                startClientX: e.clientX,
                startClientY: e.clientY,
                startXMin: chart.scales.x.min,
                startXMax: chart.scales.x.max,
                startYMin: chart.scales.y.min,
                startYMax: chart.scales.y.max,
            };
            container.style.cursor = "grabbing";
        };

        const handleMouseMove = (e) => {
            if (spaceHeldRef.current) e.stopPropagation();
            const drag = dragStateRef.current;
            const chart = chartRef.current;
            if (!drag || !chart) return;
            const dxPx = e.clientX - drag.startClientX;
            const dyPx = e.clientY - drag.startClientY;
            const dataPerPixelX = (drag.startXMax - drag.startXMin) / chart.chartArea.width;
            const dataPerPixelY = (drag.startYMax - drag.startYMin) / chart.chartArea.height;
            const shiftX = -dxPx * dataPerPixelX;
            const shiftY = dyPx * dataPerPixelY;
            const newXMin = drag.startXMin + shiftX;
            const newXMax = drag.startXMax + shiftX;
            chart.options.scales.x.min = newXMin;
            chart.options.scales.x.max = newXMax;
            chart.options.scales.y.min = drag.startYMin + shiftY;
            chart.options.scales.y.max = drag.startYMax + shiftY;
            chart.update("none");
            reportXRange(newXMin, newXMax);
        };

        const handleMouseUp = (e) => {
            if (spaceHeldRef.current || dragStateRef.current) e.stopPropagation();
            dragStateRef.current = null;
            container.style.cursor = spaceHeldRef.current ? "grab" : "";
        };

        // Space held with no drag yet still reserves the gesture entirely - a stray click while
        // Space is down shouldn't fall through to circle-selection either, matching "Space = pan
        // mode for as long as it's held" (the same convention common design tools use).
        const handleClick = (e) => {
            if (spaceHeldRef.current) e.stopPropagation();
        };

        canvas.addEventListener("wheel", handleWheel, { passive: false });
        canvas.addEventListener("gesturestart", handleGestureStart);
        canvas.addEventListener("gesturechange", handleGestureChange);
        canvas.addEventListener("gestureend", handleGestureEnd);
        container.addEventListener("mousedown", handleMouseDown, { capture: true });
        container.addEventListener("mousemove", handleMouseMove, { capture: true });
        container.addEventListener("mouseup", handleMouseUp, { capture: true });
        container.addEventListener("click", handleClick, { capture: true });

        return () => {
            canvas.removeEventListener("wheel", handleWheel);
            canvas.removeEventListener("gesturestart", handleGestureStart);
            canvas.removeEventListener("gesturechange", handleGestureChange);
            canvas.removeEventListener("gestureend", handleGestureEnd);
            container.removeEventListener("mousedown", handleMouseDown, { capture: true });
            container.removeEventListener("mousemove", handleMouseMove, { capture: true });
            container.removeEventListener("mouseup", handleMouseUp, { capture: true });
            container.removeEventListener("click", handleClick, { capture: true });
            chartRef.current = null;
            containerRef.current = null;
        };
    };

    return { attach, resetView, setXRange };
}
